/**
 * Theme Utilities
 * Functions for creating, merging, validating, and serializing themes
 * Requirements: 6.1, 6.2, 6.3, 6.5, 7.1, 7.2, 7.3
 */

import { ThemeTokensSchema, type ThemeTokens, defaultThemeTokens, type DeepPartial } from './themeTokens';

// Re-export the schema
export { ThemeTokensSchema };

/**
 * Deep merge helper for nested object merging
 * Recursively merges source into target, preserving unspecified values
 */
function deepMergeImpl(
  target: Record<string, unknown>,
  source: Record<string, unknown>
): Record<string, unknown> {
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = target[key];

      if (
        sourceValue !== null &&
        sourceValue !== undefined &&
        typeof sourceValue === 'object' &&
        !Array.isArray(sourceValue) &&
        typeof targetValue === 'object' &&
        targetValue !== null &&
        !Array.isArray(targetValue)
      ) {
        result[key] = deepMergeImpl(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>
        );
      } else if (sourceValue !== undefined) {
        result[key] = sourceValue;
      }
    }
  }

  return result;
}

/**
 * Create a complete theme from partial overrides
 * Merges partial overrides with default theme values
 * Requirements: 6.1, 6.2, 6.3
 */
export function createTheme(overrides?: DeepPartial<ThemeTokens>): ThemeTokens {
  if (!overrides) {
    return { ...defaultThemeTokens };
  }
  return deepMergeImpl(
    defaultThemeTokens as unknown as Record<string, unknown>,
    overrides as unknown as Record<string, unknown>
  ) as unknown as ThemeTokens;
}

/**
 * Merge two theme objects
 * Deep merges overrides into base theme
 * Requirements: 6.1, 6.2, 6.3
 */
export function mergeThemes(
  base: ThemeTokens,
  overrides: DeepPartial<ThemeTokens>
): ThemeTokens {
  return deepMergeImpl(
    base as unknown as Record<string, unknown>,
    overrides as unknown as Record<string, unknown>
  ) as unknown as ThemeTokens;
}

/**
 * Deep-merge two partial theme objects, with `overrides` winning over `base`.
 * Undefined values in `overrides` are skipped so base values are preserved.
 * Unlike `mergeThemes`, neither argument needs to be a complete theme — used
 * when overlaying account theme partials before handing the result to a
 * ThemeProvider (which then merges the result with the defaults).
 */
export function deepMergePartials(
  base: DeepPartial<ThemeTokens>,
  overrides: DeepPartial<ThemeTokens>
): DeepPartial<ThemeTokens> {
  return deepMergeImpl(
    base as unknown as Record<string, unknown>,
    overrides as unknown as Record<string, unknown>
  ) as unknown as DeepPartial<ThemeTokens>;
}

/**
 * Validate a theme object against the schema
 * Returns true if valid, false otherwise
 */
export function validateTheme(theme: unknown): theme is ThemeTokens {
  return ThemeTokensSchema.safeParse(theme).success;
}

/**
 * Validate and sanitize a partial theme, falling back to defaults for invalid values
 * Requirements: 6.5
 */
export function sanitizePartialTheme(
  partial: DeepPartial<ThemeTokens>
): DeepPartial<ThemeTokens> {
  // Since the new schema is robust, we rely on the createTheme process to handle defaults
  // and Zod to validate the final result.
  // For partial sanitization, we can return the partial object as-is if it's reasonably formed,
  // or we could try to validate sub-objects.
  // For backward compatibility with the previous implementation style, we'll just pass it through
  // as the deep merge + validation strategy is preferred now.
  return partial;
}

/**
 * Create a theme with validation - invalid values fall back to defaults
 * Requirements: 6.5
 */
export function createThemeWithValidation(
  overrides?: DeepPartial<ThemeTokens>
): ThemeTokens {
  if (!overrides) {
    return { ...defaultThemeTokens };
  }

  // 1. Merge overrides into default theme
  const candidate = createTheme(overrides);

  // 2. Validate the result
  const result = ThemeTokensSchema.safeParse(candidate);

  if (result.success) {
    return result.data;
  }

  // 3. If invalid, log error and return default (or potentially a best-effort sanitized version)
  console.warn('Theme validation failed, falling back to default theme', result.error);
  return { ...defaultThemeTokens };
}


// ============================================================================
// Theme Serialization
// Requirements: 7.1, 7.2, 7.3
// ============================================================================

/**
 * Theme validation error structure
 */
export interface ThemeValidationError {
  type: 'parse_error' | 'validation_error';
  message: string;
  path?: string[];
  details?: unknown;
}

/**
 * Result type for theme parsing
 */
export type ThemeParseResult =
  | { success: true; theme: ThemeTokens }
  | { success: false; error: ThemeValidationError };

/**
 * Serialize a theme to JSON string
 * Requirements: 7.1
 */
export function serializeTheme(theme: ThemeTokens): string {
  return JSON.stringify(theme);
}

/**
 * Parse and validate JSON into a ThemeTokens object
 * Returns a Result type with success/error
 * Requirements: 7.2, 7.3
 */
export function parseTheme(json: string): ThemeParseResult {
  let parsed: unknown;

  try {
    parsed = JSON.parse(json);
  } catch (e) {
    return {
      success: false,
      error: {
        type: 'parse_error',
        message: 'Invalid JSON string',
        details: e instanceof Error ? e.message : String(e),
      },
    };
  }

  const result = ThemeTokensSchema.safeParse(parsed);

  if (result.success) {
    return {
      success: true,
      theme: result.data,
    };
  }

  const firstError = result.error.errors[0];
  return {
    success: false,
    error: {
      type: 'validation_error',
      message: firstError?.message ?? 'Theme validation failed',
      path: firstError?.path.map(String),
      details: result.error.errors,
    },
  };
}
